import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import { useServiceTypes } from "../hooks/useServiceTypes";
import CustomerTimeline from "../components/CustomerTimeline";
import CustomerTags from "../components/CustomerTags";
import { DeliveryIssueWarning } from "../components/DeliveryIssueWarning";
import { formatCurrency } from "../utils/formatters";
import {
  normalizeEmail,
  isValidEmail,
  getEmailError,
  normalizeUsPhone,
  isValidUsPhone,
  getPhoneError
} from "../utils/contactValidation";
import {
  ArrowLeft,
  Phone,
  Envelope,
  MapPin,
  Briefcase,
  Receipt,
  FileText,
  PencilSimple,
  Plus,
  Trash,
  User,
  ChatCircleText,
  Star,
  GoogleLogo,
  Gear,
  Lightning,
  ClockCounterClockwise,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { isEntitled, isFeatureActive, fetched, refetch } = useEntitlementsWithFetch();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [createJobModalOpen, setCreateJobModalOpen] = useState(false);
  const [configureReviewModalOpen, setConfigureReviewModalOpen] = useState(false);
  const [upgradeReviewModalOpen, setUpgradeReviewModalOpen] = useState(false);
  const [fixAccessModalOpen, setFixAccessModalOpen] = useState(false);
  const [refreshingAccess, setRefreshingAccess] = useState(false);
  const [sendingReview, setSendingReview] = useState(false);
  
  // Check if review collection feature is available and entitled
  const reviewFeatureActive = isFeatureActive("review_collection");
  const hasReviewEntitlement = isEntitled("review_collection");

  // Edit form
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
    tags: [],
    addresses: [],
    preferred_communication: "email",
  });

  // New address form
  const [newAddress, setNewAddress] = useState({
    label: "Home",
    street: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
  });
  
  // Address management state
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressLastUsed, setAddressLastUsed] = useState({});
  const [deleteAddressConfirm, setDeleteAddressConfirm] = useState(null);
  const [addressReferences, setAddressReferences] = useState(null);
  const [checkingReferences, setCheckingReferences] = useState(false);
  const [addressErrors, setAddressErrors] = useState({});
  
  // Delete blocked modal state
  const [deleteBlockedModalOpen, setDeleteBlockedModalOpen] = useState(false);

  // Service types hook
  const { 
    serviceTypes, 
    loading: serviceTypesLoading, 
    getDefaultServiceType,
    updateUserPreference,
    getIdBySlug,
  } = useServiceTypes();
  
  // Track if user has touched the service type field
  const userTouchedServiceType = useRef(false);
  const defaultsApplied = useRef(false);

  // New job form
  const [newJob, setNewJob] = useState({
    title: "",
    description: "",
    service_type: "",
    priority: "normal",
  });

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  // Apply default service type when dialog opens and service types are loaded
  useEffect(() => {
    if (createJobModalOpen && serviceTypes.length > 0 && !userTouchedServiceType.current && !defaultsApplied.current) {
      const defaultType = getDefaultServiceType();
      if (defaultType && !newJob.service_type) {
        setNewJob(prev => ({ ...prev, service_type: defaultType }));
      }
      defaultsApplied.current = true;
    }
  }, [createJobModalOpen, serviceTypes, getDefaultServiceType, newJob.service_type]);

  // Reset flags when dialog closes
  useEffect(() => {
    if (!createJobModalOpen) {
      userTouchedServiceType.current = false;
      defaultsApplied.current = false;
    }
  }, [createJobModalOpen]);

  // Handle service type change
  const handleServiceTypeChange = (value) => {
    userTouchedServiceType.current = true;
    setNewJob({ ...newJob, service_type: value });
  };

  const fetchCustomer = async () => {
    try {
      const response = await fetch(`${API}/customers/${customerId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
        setEditForm({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          company: data.company || "",
          notes: data.notes || "",
          tags: data.tags || [],
          addresses: data.addresses || [],
          preferred_communication: data.preferred_communication || "email",
        });
        
        // Fetch last-used timestamps for each address
        if (data.addresses?.length > 0) {
          const lastUsedData = {};
          await Promise.all(
            data.addresses.map(async (addr) => {
              try {
                const lastUsedRes = await fetch(`${API}/customers/${customerId}/addresses/${addr.id}/last-used`, {
                  credentials: "include",
                });
                if (lastUsedRes.ok) {
                  const lastUsed = await lastUsedRes.json();
                  lastUsedData[addr.id] = lastUsed;
                }
              } catch (error) {
                console.error("Error fetching last-used for address:", error);
              }
            })
          );
          setAddressLastUsed(lastUsedData);
        }
      }
    } catch (error) {
      console.error("Error fetching customer:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveCustomer = async () => {
    // Validate email
    const normalizedEmail = normalizeEmail(editForm.email);
    if (normalizedEmail && !isValidEmail(normalizedEmail)) {
      const emailError = getEmailError(normalizedEmail);
      toast.error(emailError || "Please enter a valid email like name@example.com");
      return;
    }
    
    // Validate phone
    const phoneInput = editForm.phone?.trim();
    if (phoneInput && !isValidUsPhone(phoneInput)) {
      const phoneError = getPhoneError(phoneInput);
      toast.error(phoneError || "Please enter a 10-digit US phone number");
      return;
    }
    
    // Normalize values
    const normalizedPhone = normalizeUsPhone(phoneInput);
    
    try {
      const response = await fetch(`${API}/customers/${customerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...editForm,
          email: normalizedEmail || null,
          phone: normalizedPhone || null
        }),
      });
      if (response.ok) {
        toast.success("Customer updated");
        setEditModalOpen(false);
        fetchCustomer();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update customer");
      }
    } catch (error) {
      toast.error("Failed to update customer");
    }
  };

  // ========================
  // ADDRESS MANAGEMENT
  // ========================

  const openAddAddressModal = () => {
    setEditingAddress(null);
    setNewAddress({ label: "Home", street: "", address_line2: "", city: "", state: "", zip: "" });
    setAddressModalOpen(true);
  };

  const openEditAddressModal = (address) => {
    setEditingAddress(address);
    setNewAddress({
      label: address.label || "",
      street: address.street || "",
      address_line2: address.address_line2 || "",
      city: address.city || "",
      state: address.state || "",
      zip: address.zip || "",
    });
    setAddressModalOpen(true);
  };

  const saveAddress = async () => {
    // Validate all required fields with inline errors
    const errors = {};
    
    if (!newAddress.street?.trim()) {
      errors.street = "Street address is required";
    }
    if (!newAddress.city?.trim()) {
      errors.city = "City is required";
    }
    if (!newAddress.state?.trim()) {
      errors.state = "Required";
    } else if (newAddress.state.trim().length !== 2) {
      errors.state = "Use 2-letter code";
    }
    if (!newAddress.zip?.trim()) {
      errors.zip = "ZIP is required";
    } else if (!/^\d{5}(-\d{4})?$/.test(newAddress.zip.trim())) {
      errors.zip = "Invalid ZIP format";
    }
    
    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors);
      return;
    }
    
    setAddressErrors({});

    try {
      if (editingAddress) {
        // Update existing address
        const response = await fetch(`${API}/customers/${customerId}/addresses/${editingAddress.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(newAddress),
        });
        if (response.ok) {
          toast.success("Address updated");
          setAddressModalOpen(false);
          setEditingAddress(null);
          fetchCustomer();
        } else {
          toast.error("Failed to update address");
        }
      } else {
        // Add new address
        const response = await fetch(`${API}/customers/${customerId}/addresses`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(newAddress),
        });
        if (response.ok) {
          toast.success("Address added");
          setAddressModalOpen(false);
          fetchCustomer();
        } else {
          toast.error("Failed to add address");
        }
      }
    } catch (error) {
      toast.error("Failed to save address");
    }
  };

  const checkAddressReferences = async (address) => {
    setCheckingReferences(true);
    setDeleteAddressConfirm(address);
    
    try {
      const response = await fetch(`${API}/customers/${customerId}/addresses/${address.id}/references`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setAddressReferences(data);
      }
    } catch (error) {
      console.error("Error checking references:", error);
      setAddressReferences({ has_references: false, references: { jobs: 0, visits: 0, invoices: 0 } });
    } finally {
      setCheckingReferences(false);
    }
  };

  const deleteAddress = async () => {
    if (!deleteAddressConfirm) return;
    
    try {
      const response = await fetch(`${API}/customers/${customerId}/addresses/${deleteAddressConfirm.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Address deleted");
        setDeleteAddressConfirm(null);
        setAddressReferences(null);
        fetchCustomer();
      } else {
        const error = await response.json();
        toast.error(error.detail?.message || "Failed to delete address");
      }
    } catch (error) {
      toast.error("Failed to delete address");
    }
  };

  const closeDeleteModal = () => {
    setDeleteAddressConfirm(null);
    setAddressReferences(null);
  };

  // Legacy functions for edit modal (keep for backwards compatibility)
  const addAddress = () => {
    if (!newAddress.street) {
      toast.error("Please enter a street address");
      return;
    }
    const updatedAddresses = [...editForm.addresses, { ...newAddress, id: Date.now() }];
    setEditForm({ ...editForm, addresses: updatedAddresses });
    setNewAddress({ label: "Home", street: "", address_line2: "", city: "", state: "", zip: "" });
    setAddressModalOpen(false);
  };

  const removeAddress = (index) => {
    const updatedAddresses = editForm.addresses.filter((_, i) => i !== index);
    setEditForm({ ...editForm, addresses: updatedAddresses });
  };

  const deleteCustomer = async () => {
    // Use XMLHttpRequest to bypass platform's fetch interceptor
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("DELETE", `${API}/customers/${customerId}`);
        xhr.withCredentials = true;
        xhr.onload = () => {
          try {
            const data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
            resolve({ status: xhr.status, data });
          } catch {
            resolve({ status: xhr.status, data: {} });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send();
      });
      
      if (result.status >= 200 && result.status < 300) {
        toast.success("Customer deleted");
        navigate("/customers");
        return;
      }
      
      // Check for blocked deletion due to history
      if (result.status === 409 && result.data.error === "CUSTOMER_DELETE_BLOCKED_HISTORY_EXISTS") {
        setDeleteBlockedModalOpen(true);
        return;
      }
      
      // Generic error
      toast.error("Failed to delete customer");
    } catch (error) {
      toast.error("Failed to delete customer");
    }
  };

  const createJob = async () => {
    if (!newJob.title) {
      toast.error("Please enter a job title");
      return;
    }
    try {
      const response = await fetch(`${API}/jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          customer_id: customerId,
          ...newJob,
          status: "draft",
        }),
      });
      if (response.ok) {
        const job = await response.json();
        toast.success("Job created");
        
        // Update user preference with the selected service type
        const serviceTypeId = getIdBySlug(newJob.service_type);
        if (serviceTypeId) {
          updateUserPreference(serviceTypeId);
        }
        
        setCreateJobModalOpen(false);
        setNewJob({ title: "", description: "", service_type: "", priority: "normal" });
        navigate(`/jobs/${job.job_id}`);
      } else {
        toast.error("Failed to create job");
      }
    } catch (error) {
      toast.error("Failed to create job");
    }
  };

  const sendReviewRequest = async () => {
    // REMOVED frontend pre-gate - let backend handle entitlement check
    // Backend uses "sync on deny" pattern which auto-heals stale entitlements
    // See /api/reviews/request for canonical entitlement logic
    
    setSendingReview(true);
    try {
      // Use XMLHttpRequest to avoid any fetch interceptors
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/reviews/request`);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data });
          } catch {
            resolve({ status: xhr.status, data: { detail: xhr.responseText } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(JSON.stringify({ customer_id: customerId }));
      });
      
      if (result.status === 200 && result.data.success) {
        toast.success(`Review request sent to ${result.data.customer_name} via ${result.data.send_result?.method || "email"}!`);
      } else if (result.status === 400 && (result.data.detail?.includes("Google Business") || result.data.detail?.includes("configure"))) {
        // Google Business not configured - show modal
        setConfigureReviewModalOpen(true);
      } else if (result.status === 403 && result.data.detail?.includes("not enabled")) {
        // Entitlement error - check for drift
        // Frontend shows entitled but backend says no - this is drift
        if (hasReviewEntitlement) {
          // DRIFT DETECTED - show fix access modal
          setFixAccessModalOpen(true);
        } else {
          // Truly not entitled - show upgrade modal
          setUpgradeReviewModalOpen(true);
        }
      } else {
        toast.error(result.data.detail || result.data.message || "Failed to send review request");
      }
    } catch (error) {
      console.error("Review request error:", error);
      toast.error("Failed to send review request. Please try again.");
    } finally {
      setSendingReview(false);
    }
  };
  
  // Refresh entitlements to fix drift
  const handleRefreshAccess = async () => {
    setRefreshingAccess(true);
    try {
      const response = await fetch(`${API}/entitlements/refresh`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.refreshed) {
          if (data.drift_detected) {
            toast.success("Access restored! Retrying your request...");
          } else {
            toast.success("Access refreshed.");
          }
          // Refresh frontend entitlements
          if (refetch) {
            await refetch(true);
          }
          setFixAccessModalOpen(false);
          // Auto-retry the review request after a short delay
          setTimeout(() => sendReviewRequest(), 300);
        } else {
          // Throttled - show message
          toast.info(data.message || "Please wait a moment and try again.");
        }
      } else {
        toast.error("Failed to refresh access. Please reload the page.");
      }
    } catch (error) {
      console.error("Refresh access error:", error);
      toast.error("Failed to refresh access. Please reload the page.");
    } finally {
      setRefreshingAccess(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
      scheduled: "bg-blue-50 text-blue-700 border-blue-300",
      in_progress: "bg-amber-50 text-amber-700 border-amber-300",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-300",
      sent: "bg-blue-50 text-blue-700 border-blue-300",
      paid: "bg-emerald-50 text-emerald-700 border-emerald-300",
      approved: "bg-emerald-50 text-emerald-700 border-emerald-300",
      accepted: "bg-emerald-50 text-emerald-700 border-emerald-300", // Same as approved (legacy)
      declined: "bg-red-50 text-red-700 border-red-300",
      converted: "bg-purple-50 text-purple-700 border-purple-300",
      void: "bg-zinc-200 text-zinc-500 border-zinc-400",
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-zinc-200 rounded-sm" />
        <div className="h-48 bg-zinc-200 rounded-sm" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">Customer not found</p>
        <Link to="/customers">
          <Button variant="link" className="text-[#FF6600]">Back to Customers</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/customers">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft size={18} />
            Back to Customers
          </Button>
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-2 gap-2" onClick={() => setEditModalOpen(true)} data-testid="edit-customer-btn">
            <PencilSimple size={16} />
            Edit
          </Button>
          <Button variant="outline" size="sm" className="border-2 gap-2 text-red-600 hover:bg-red-50" onClick={deleteCustomer}>
            <Trash size={16} />
            Delete
          </Button>
        </div>
      </div>

      {/* Customer Info Card */}
      <Card className="border-2 border-zinc-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-[#FF6600]/10 border-2 border-[#FF6600]/30 flex items-center justify-center rounded-sm">
                <User size={32} className="text-[#FF6600]" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold uppercase">{customer.name}</h1>
                {customer.company && <p className="text-zinc-500">{customer.company}</p>}
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="flex items-center gap-2 text-zinc-600 hover:text-[#FF6600]">
                      <Phone size={16} />
                      {customer.phone}
                    </a>
                  )}
                  {customer.email && (
                    <a href={`mailto:${customer.email}`} className="flex items-center gap-2 text-zinc-600 hover:text-[#FF6600]">
                      <Envelope size={16} />
                      {customer.email}
                    </a>
                  )}
                  <span className="flex items-center gap-2 text-zinc-600">
                    {customer.preferred_communication === "sms" ? (
                      <><ChatCircleText size={16} className="text-blue-500" /> Prefers SMS</>
                    ) : (
                      <><Envelope size={16} className="text-emerald-500" /> Prefers Email</>
                    )}
                  </span>
                </div>
                {/* Delivery Issue Warnings */}
                <div className="mt-4 space-y-2">
                  <DeliveryIssueWarning
                    customerId={customerId}
                    channel="email"
                    currentValue={customer.email}
                    onFixed={() => fetchCustomer()}
                  />
                  <DeliveryIssueWarning
                    customerId={customerId}
                    channel="sms"
                    currentValue={customer.phone}
                    onFixed={() => fetchCustomer()}
                  />
                </div>
                {/* Customer Tags - Interactive */}
                <div className="mt-4 pt-3 border-t border-zinc-100">
                  <p className="text-xs text-zinc-400 uppercase mb-2">Tags</p>
                  <CustomerTags 
                    customerId={customerId} 
                    initialTags={customer.tags || []}
                    onTagsChange={(newTags) => setCustomer(prev => ({ ...prev, tags: newTags }))}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {reviewFeatureActive && (
                <Button 
                  variant="outline" 
                  onClick={sendReviewRequest} 
                  disabled={sendingReview}
                  className="border-2 gap-2" 
                  data-testid="request-review-btn"
                >
                  <Star size={18} />
                  {sendingReview ? "Sending..." : "Request Review"}
                  {!hasReviewEntitlement && <Lightning size={14} className="text-[#FF6600]" />}
                </Button>
              )}
              <Button onClick={() => setCreateJobModalOpen(true)} className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2" data-testid="create-job-btn">
                <Plus size={18} />
                Create Job
              </Button>
            </div>
          </div>

          {/* Addresses Section - Full Management UI */}
          <div className="mt-6 pt-6 border-t border-zinc-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-sm text-zinc-500 uppercase">Addresses</h3>
              <Button 
                size="sm" 
                onClick={openAddAddressModal}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-1 h-8"
                data-testid="add-address-btn"
              >
                <Plus size={14} />
                Add Address
              </Button>
            </div>
            
            {customer.addresses?.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-3">
                {customer.addresses.map((addr) => (
                  <div key={addr.id} className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid={`address-card-${addr.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-xs text-zinc-400 uppercase mb-1 flex items-center gap-2">
                          <MapPin size={12} />
                          {addr.label || "Address"}
                        </p>
                        <p className="text-sm font-medium">
                          {addr.street}
                          {addr.address_line2 && <span className="block text-zinc-500">{addr.address_line2}</span>}
                        </p>
                        <p className="text-sm text-zinc-600">
                          {addr.city && `${addr.city}, `}
                          {addr.state && `${addr.state} `}
                          {addr.zip}
                        </p>
                        {addressLastUsed[addr.id]?.last_used && (
                          <p className="text-xs text-zinc-400 mt-2">
                            Last used: {new Date(addressLastUsed[addr.id].last_used).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditAddressModal(addr)}
                          className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-700"
                          data-testid={`edit-address-${addr.id}`}
                        >
                          <PencilSimple size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => checkAddressReferences(addr)}
                          className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600"
                          data-testid={`delete-address-${addr.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 bg-zinc-50 border border-zinc-200 rounded-sm text-center">
                <MapPin size={32} className="mx-auto text-zinc-300 mb-2" />
                <p className="text-zinc-500 font-medium">No addresses added</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Addresses are required for scheduling visits and creating invoices.
                </p>
                <Button 
                  size="sm" 
                  onClick={openAddAddressModal}
                  className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-1 mt-4"
                >
                  <Plus size={14} />
                  Add First Address
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Timeline, Jobs, Invoices, Estimates */}
      <Tabs defaultValue="timeline" className="space-y-6">
        <TabsList className="border-2 border-zinc-200 p-1 bg-zinc-50">
          <TabsTrigger value="timeline" className="data-[state=active]:bg-white" data-testid="timeline-tab">
            <ClockCounterClockwise size={16} className="mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="jobs" className="data-[state=active]:bg-white" data-testid="jobs-tab">
            <Briefcase size={16} className="mr-2" />
            Jobs ({customer.jobs?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-white" data-testid="invoices-tab">
            <Receipt size={16} className="mr-2" />
            Invoices ({customer.invoices?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="estimates" className="data-[state=active]:bg-white" data-testid="estimates-tab">
            <FileText size={16} className="mr-2" />
            Estimates ({customer.estimates?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <CustomerTimeline customerId={customerId} customerName={customer.name} onNoteAdded={fetchCustomer} />
        </TabsContent>

        <TabsContent value="jobs">
          {customer.jobs?.length > 0 ? (
            <div className="space-y-3">
              {customer.jobs.map((job) => (
                <Link key={job.job_id} to={`/jobs/${job.job_id}`}>
                  <Card className="border-2 border-zinc-200 card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{job.title}</p>
                          <p className="text-sm text-zinc-500">{new Date(job.created_at).toLocaleDateString()}</p>
                        </div>
                        <Badge variant="outline" className={getStatusColor(job.status)}>{job.status.replace("_", " ")}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-zinc-300">
              <CardContent className="p-8 text-center">
                <Briefcase size={32} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-zinc-400 mb-4">No jobs for this customer</p>
                <Button onClick={() => setCreateJobModalOpen(true)} className="bg-[#FF6600] hover:bg-[#CC5200] text-white">
                  <Plus size={16} className="mr-2" />
                  Create First Job
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="invoices">
          {customer.invoices?.length > 0 ? (
            <div className="space-y-3">
              {customer.invoices.map((invoice) => (
                <Card key={invoice.invoice_id} className="border-2 border-zinc-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs text-zinc-400">{invoice.invoice_number}</p>
                        <p className="font-medium">{invoice.title}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-lg font-bold">{formatCurrency(invoice.total)}</p>
                        <Badge variant="outline" className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-zinc-300">
              <CardContent className="p-8 text-center text-zinc-400">
                <Receipt size={32} className="mx-auto mb-2 text-zinc-300" />
                No invoices yet
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="estimates">
          {customer.estimates?.length > 0 ? (
            <div className="space-y-3">
              {customer.estimates.map((estimate) => (
                <Card key={estimate.estimate_id} className="border-2 border-zinc-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-mono text-xs text-zinc-400">{estimate.estimate_number}</p>
                        <p className="font-medium">{estimate.title}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-lg font-bold">{formatCurrency(estimate.total)}</p>
                        <Badge variant="outline" className={getStatusColor(estimate.status)}>{estimate.status}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-2 border-dashed border-zinc-300">
              <CardContent className="p-8 text-center text-zinc-400">
                <FileText size={32} className="mx-auto mb-2 text-zinc-300" />
                No estimates yet
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Customer Profile Notes (legacy single field) */}
      {customer.notes && (
        <Card className="border-2 border-zinc-200">
          <CardHeader className="py-4 border-b border-zinc-100">
            <CardTitle className="font-heading text-lg uppercase">Customer Profile Notes</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <p className="text-zinc-600 whitespace-pre-wrap">{customer.notes}</p>
            <p className="text-xs text-zinc-400 mt-2">Edit this in customer settings</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Customer Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-customer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Edit Customer</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 h-12 border-2" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1 h-12 border-2" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1 h-12 border-2" />
              </div>
              <div>
                <Label>Company</Label>
                <Input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} className="mt-1 h-12 border-2" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1 border-2" rows={3} />
              </div>
              <div>
                <Label>Preferred Contact Method</Label>
                <Select 
                  value={editForm.preferred_communication} 
                  onValueChange={(value) => setEditForm({ ...editForm, preferred_communication: value })}
                >
                  <SelectTrigger className="mt-1 h-12 border-2">
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">
                      <span className="flex items-center gap-2">
                        <Envelope size={16} /> Email
                      </span>
                    </SelectItem>
                    <SelectItem value="sms">
                      <span className="flex items-center gap-2">
                        <ChatCircleText size={16} /> SMS Text
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Addresses */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Addresses</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAddressModalOpen(true)} className="text-[#FF6600]">
                    <Plus size={14} className="mr-1" />
                    Add
                  </Button>
                </div>
                {editForm.addresses?.length > 0 ? (
                  <div className="space-y-2">
                    {editForm.addresses.map((addr, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-zinc-50 border border-zinc-200 rounded-md">
                        <div className="text-sm">
                          <span className="text-zinc-400">{addr.label}: </span>
                          {addr.street}, {addr.city}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeAddress(idx)} className="text-red-500 h-6 w-6 p-0">
                          <Trash size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">No addresses</p>
                )}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="flex-1 sm:flex-none border-2">Cancel</Button>
            <Button onClick={saveCustomer} className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Address Modal */}
      <Dialog open={addressModalOpen} onOpenChange={(open) => {
        if (!open) {
          setEditingAddress(null);
          setNewAddress({ label: "Home", street: "", address_line2: "", city: "", state: "", zip: "" });
          setAddressErrors({});
        }
        setAddressModalOpen(open);
      }}>
        <DialogContent className="sm:max-w-md" data-testid="address-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <MapPin size={20} weight="bold" />
              {editingAddress ? "Edit Address" : "Add Address"}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form id="address-form" onSubmit={(e) => { e.preventDefault(); saveAddress(); }} className="space-y-4">
              <div>
                <Label htmlFor="address-label">Label</Label>
                <Input 
                  id="address-label"
                  value={newAddress.label} 
                  onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })} 
                  placeholder="Home, Office, etc." 
                  className="mt-1 h-12 border-2" 
                  data-testid="address-label-input"
                />
              </div>
              <div>
                <Label htmlFor="address-street">Street Address *</Label>
                <Input 
                  id="address-street"
                  value={newAddress.street} 
                  onChange={(e) => {
                    setNewAddress({ ...newAddress, street: e.target.value });
                    if (addressErrors.street) setAddressErrors(prev => ({ ...prev, street: "" }));
                  }}
                  placeholder="123 Main St" 
                  className={`mt-1 h-12 border-2 ${addressErrors.street ? 'border-red-500' : ''}`}
                  autoFocus
                  data-testid="address-street-input"
                />
                {addressErrors.street && (
                  <p className="mt-1 text-sm text-red-500">{addressErrors.street}</p>
                )}
              </div>
              <div>
                <Label htmlFor="address-line2">Address Line 2</Label>
                <Input 
                  id="address-line2"
                  value={newAddress.address_line2 || ""} 
                  onChange={(e) => setNewAddress({ ...newAddress, address_line2: e.target.value })} 
                  placeholder="Apt, Suite, Unit, etc." 
                  className="mt-1 h-12 border-2" 
                  data-testid="address-line2-input"
                />
              </div>
              <div className="grid grid-cols-6 gap-3">
                <div className="col-span-3">
                  <Label htmlFor="address-city">City *</Label>
                  <Input 
                    id="address-city"
                    value={newAddress.city} 
                    onChange={(e) => {
                      setNewAddress({ ...newAddress, city: e.target.value });
                      if (addressErrors.city) setAddressErrors(prev => ({ ...prev, city: "" }));
                    }}
                    placeholder="Boston"
                    className={`mt-1 h-12 border-2 ${addressErrors.city ? 'border-red-500' : ''}`}
                    data-testid="address-city-input"
                  />
                  {addressErrors.city && (
                    <p className="mt-1 text-sm text-red-500">{addressErrors.city}</p>
                  )}
                </div>
                <div className="col-span-1">
                  <Label htmlFor="address-state">State *</Label>
                  <Input 
                    id="address-state"
                    value={newAddress.state} 
                    onChange={(e) => {
                      setNewAddress({ ...newAddress, state: e.target.value.toUpperCase() });
                      if (addressErrors.state) setAddressErrors(prev => ({ ...prev, state: "" }));
                    }}
                    maxLength={2}
                    placeholder="MA"
                    className={`mt-1 h-12 border-2 uppercase ${addressErrors.state ? 'border-red-500' : ''}`}
                    data-testid="address-state-input"
                  />
                  {addressErrors.state && (
                    <p className="mt-1 text-sm text-red-500">{addressErrors.state}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address-zip">ZIP *</Label>
                  <Input 
                    id="address-zip"
                    value={newAddress.zip} 
                    onChange={(e) => {
                      setNewAddress({ ...newAddress, zip: e.target.value });
                      if (addressErrors.zip) setAddressErrors(prev => ({ ...prev, zip: "" }));
                    }}
                    maxLength={10}
                    placeholder="02101"
                    className={`mt-1 h-12 border-2 ${addressErrors.zip ? 'border-red-500' : ''}`}
                    data-testid="address-zip-input"
                  />
                  {addressErrors.zip && (
                    <p className="mt-1 text-sm text-red-500">{addressErrors.zip}</p>
                  )}
                </div>
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressModalOpen(false)} className="flex-1 sm:flex-none border-2">
              Cancel
            </Button>
            <Button 
              type="submit" 
              form="address-form"
              className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="address-save-btn"
            >
              {editingAddress ? "Save Changes" : "Add Address"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Address Confirmation Modal */}
      <Dialog open={!!deleteAddressConfirm} onOpenChange={(open) => !open && closeDeleteModal()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Delete Address</DialogTitle>
          </DialogHeader>
          {checkingReferences ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-3 border-[#FF6600] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-zinc-500 mt-2">Checking references...</p>
            </div>
          ) : addressReferences?.has_references ? (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
                <p className="font-medium text-amber-800">Cannot delete this address</p>
                <p className="text-sm text-amber-700 mt-1">
                  This address is still being used by:
                </p>
                <ul className="text-sm text-amber-700 mt-2 space-y-1">
                  {addressReferences.references.jobs > 0 && (
                    <li>• {addressReferences.references.jobs} job(s)</li>
                  )}
                  {addressReferences.references.visits > 0 && (
                    <li>• {addressReferences.references.visits} visit(s)</li>
                  )}
                  {addressReferences.references.invoices > 0 && (
                    <li>• {addressReferences.references.invoices} invoice(s)</li>
                  )}
                </ul>
                <p className="text-sm text-amber-700 mt-3">
                  Please reassign these records to a different address before deleting.
                </p>
              </div>
              <div className="flex justify-end">
                <Button variant="outline" onClick={closeDeleteModal} className="border-2">
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <p className="text-zinc-600">
                Are you sure you want to delete this address?
              </p>
              {deleteAddressConfirm && (
                <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                  <p className="text-xs text-zinc-400 uppercase">{deleteAddressConfirm.label || "Address"}</p>
                  <p className="text-sm font-medium">
                    {deleteAddressConfirm.street}
                    {deleteAddressConfirm.city && `, ${deleteAddressConfirm.city}`}
                    {deleteAddressConfirm.state && `, ${deleteAddressConfirm.state}`}
                    {deleteAddressConfirm.zip && ` ${deleteAddressConfirm.zip}`}
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={closeDeleteModal} className="flex-1 border-2">
                  Cancel
                </Button>
                <Button onClick={deleteAddress} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                  Delete Address
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Job Modal */}
      <Dialog open={createJobModalOpen} onOpenChange={setCreateJobModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-job-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Create Job for {customer.name}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form id="customer-job-form" onSubmit={(e) => { e.preventDefault(); createJob(); }} className="space-y-4">
              <div>
                <Label>Job Title *</Label>
                <Input value={newJob.title} onChange={(e) => setNewJob({ ...newJob, title: e.target.value })} placeholder="Water heater installation" className="mt-1 h-12 border-2" data-testid="new-job-title" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={newJob.description} onChange={(e) => setNewJob({ ...newJob, description: e.target.value })} placeholder="Job details..." className="mt-1 border-2" rows={3} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Type</Label>
                  <Select
                    value={newJob.service_type}
                    onValueChange={handleServiceTypeChange}
                    disabled={serviceTypesLoading}
                  >
                    <SelectTrigger className="mt-1 h-12 border-2" data-testid="service-type-select">
                      <SelectValue placeholder={serviceTypesLoading ? "Loading..." : "Select service type"} />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.map((type) => (
                        <SelectItem key={type.service_type_id} value={type.slug}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={newJob.priority}
                    onValueChange={(v) => setNewJob({ ...newJob, priority: v })}
                  >
                    <SelectTrigger className="mt-1 h-12 border-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateJobModalOpen(false)} className="flex-1 sm:flex-none border-2">
              Cancel
            </Button>
            <Button type="submit" form="customer-job-form" disabled={!newJob.title} className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white" data-testid="confirm-create-job">
              Create Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configure Review Settings Modal */}
      <Dialog open={configureReviewModalOpen} onOpenChange={setConfigureReviewModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Gear size={24} className="text-[#FF6600]" />
              Configure Review Collection
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              To send review requests, you need to set up your Google Business listing first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <GoogleLogo size={24} className="text-blue-600 mt-0.5" weight="bold" />
                <div>
                  <p className="font-medium text-blue-800">Google Business Profile Required</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Connect your Google Business listing so customers can leave reviews on Google when they receive your review request.
                  </p>
                </div>
              </div>
            </div>
            <div className="text-sm text-zinc-600">
              <p className="font-medium mb-2">Quick setup:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to Review Settings</li>
                <li>Search for your business on Google Maps</li>
                <li>Copy and paste your Google Business URL</li>
              </ol>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setConfigureReviewModalOpen(false)} className="flex-1 border-2">
                Later
              </Button>
              <Button 
                onClick={() => navigate("/settings/reviews")} 
                className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                Configure Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade Review Feature Modal - ACTION-BASED GATING */}
      {/* Shown only when user attempts to SEND a review request without entitlement */}
      <Dialog open={upgradeReviewModalOpen} onOpenChange={setUpgradeReviewModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="review-request-upgrade-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Star size={24} className="text-[#FF6600]" weight="fill" />
              Collect More 5-Star Reviews
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Enable Review Collection to request and track customer reviews.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="p-4 bg-gradient-to-r from-[#FF6600]/10 to-amber-50 border border-[#FF6600]/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Star size={24} className="text-[#FF6600] mt-0.5" weight="fill" />
                <div>
                  <p className="font-medium text-zinc-800">What you&apos;ll get:</p>
                  <ul className="text-sm text-zinc-600 mt-2 space-y-1">
                    <li>• Send review requests via Email or SMS</li>
                    <li>• Beautiful customer-facing review landing page</li>
                    <li>• Collect Google reviews or private feedback</li>
                    <li>• Track review request performance</li>
                  </ul>
                </div>
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeReviewModalOpen(false)} className="flex-1 sm:flex-none border-2">
              Cancel
            </Button>
            <Button 
              onClick={() => navigate("/settings/upgrades?feature=review_collection")} 
              className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="review-request-upgrade-btn"
            >
              View Plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix Access Modal - Shown when drift detected (UI says entitled but backend says no) */}
      <Dialog open={fixAccessModalOpen} onOpenChange={setFixAccessModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="fix-access-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <ClockCounterClockwise size={24} className="text-blue-500" />
              Refreshing Your Access
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Your subscription looks active, but access hasn&apos;t synced yet.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                This can happen after a recent purchase or subscription change. 
                Click refresh to sync your access instantly.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setFixAccessModalOpen(false)} 
              className="flex-1 sm:flex-none border-2"
              disabled={refreshingAccess}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRefreshAccess}
              disabled={refreshingAccess}
              className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="refresh-access-btn"
            >
              {refreshingAccess ? (
                <>
                  <ClockCounterClockwise size={18} className="mr-2 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <ClockCounterClockwise size={18} className="mr-2" />
                  Refresh Access
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Blocked Modal - Customer has historical records */}
      <Dialog open={deleteBlockedModalOpen} onOpenChange={setDeleteBlockedModalOpen}>
        <DialogContent className="sm:max-w-[480px] p-8" data-testid="delete-blocked-modal">
          <div className="flex flex-col items-center text-center space-y-5">
            {/* Warning Icon */}
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="currentColor" 
                className="w-6 h-6 text-amber-500"
              >
                <path 
                  fillRule="evenodd" 
                  d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" 
                  clipRule="evenodd" 
                />
              </svg>
            </div>
            
            {/* Title */}
            <h2 className="text-xl font-semibold text-zinc-900">
              Cannot Delete Customer
            </h2>
            
            {/* Body Text */}
            <div className="space-y-3 text-zinc-600 leading-relaxed">
              <p>
                This customer has jobs, invoices, or other historical records connected to it.
              </p>
              <p>
                To protect your business records, customers with history cannot be deleted.
              </p>
              <p className="text-sm text-zinc-500">
                You can archive the customer instead.
              </p>
            </div>
            
            {/* Close Button */}
            <div className="pt-2 w-full flex justify-end">
              <Button 
                onClick={() => setDeleteBlockedModalOpen(false)}
                className="px-4 py-2.5 rounded-lg"
                data-testid="delete-blocked-close-btn"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
